// =====================================================
// File: radio-state.js
// Shared reactive proxy + observer registry
// Exported symbols:
//    RADIO_GROUP_STATE, add_radio_observer, remove_radio_observer
// =====================================================

// Internal state container
const RADIO_GROUP_STATE_TARGET = Object.create(null);

// Observer registry per groupID (Set for dedup)
const RADIO_GROUP_OBSERVERS = new Map();

const RADIO_STATE_HANDLER = {
	set(target, groupID, new_value, receiver) {
		const old_value = target[groupID];
		
		// If identical (strict) value, do nothing
		if (old_value === new_value) {
			return true;
		}
		
		if (new_value === null || new_value === undefined) {
			// treat null/undefined as clearing selection
			Reflect.deleteProperty(target, groupID);
		} else {
			Reflect.set(target, groupID, new_value, receiver);
		}
		
		// Dispatch observers (safe try/catch so one bad observer doesn't stop others)
		const observers = RADIO_GROUP_OBSERVERS.get(groupID);
		if (observers) {
			const payload = {
				groupID,
				selected_value: new_value === undefined ? null : new_value
			};
			observers.forEach(cb => {
				try {
					cb(payload);
				} catch (err) {
					console.error('RADIO observer error', err);
				}
			});
		}
		
		return true;
	},
	get(target, prop, receiver) {
		const value = Reflect.get(target, prop, receiver);
		return typeof value === 'function' ? value.bind(target) : value;
	},
	deleteProperty(target, groupID) {
		const existed = groupID in target;
		Reflect.deleteProperty(target, groupID);
		const observers = RADIO_GROUP_OBSERVERS.get(groupID);
		if (observers) {
			observers.forEach(cb => {
				try { cb({ groupID, selected_value: null }); } catch (err) { console.error('RADIO observer error', err); }
			});
		}
		return existed;
	}
};

export const RADIO_GROUP_STATE = new Proxy(RADIO_GROUP_STATE_TARGET, RADIO_STATE_HANDLER);

export function add_radio_observer(groupID, cb) {
	if (!groupID) {
		return;
	}
	if (!RADIO_GROUP_OBSERVERS.has(groupID)) {
		RADIO_GROUP_OBSERVERS.set(groupID, new Set());
	}
	RADIO_GROUP_OBSERVERS.get(groupID).add(cb);
}

export function remove_radio_observer(groupID, cb) {
	if (!groupID) {
		return;
	}
	const set = RADIO_GROUP_OBSERVERS.get(groupID);
	if (!set) {
		return;
	}
	set.delete(cb);
	if (set.size === 0) {
		RADIO_GROUP_OBSERVERS.delete(groupID);
		// cleanup state for the group
		Reflect.deleteProperty(RADIO_GROUP_STATE, groupID);
	}
}